/**
 * @packageDocumentation
 * @module      LoggedInUser.ts
 * @description Encapsulates information about the user who is logged in.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RoleName } from "./UserDetails";
import Fetcher from "./Fetcher";
import CloudApi from "./CloudApi";
/**
 * Enumerates the possible results of an attempt to validate a username/password combination.
 */
export var LoginResultCode;
(function (LoginResultCode) {
    LoginResultCode[LoginResultCode["NotAttempted"] = 0] = "NotAttempted";
    LoginResultCode[LoginResultCode["Success"] = 1] = "Success";
    LoginResultCode[LoginResultCode["InvalidUsername"] = 2] = "InvalidUsername";
    LoginResultCode[LoginResultCode["WrongPassword"] = 3] = "WrongPassword";
    LoginResultCode[LoginResultCode["PasswordExpired"] = 4] = "PasswordExpired";
    LoginResultCode[LoginResultCode["AccountLocked"] = 5] = "AccountLocked";
    LoginResultCode[LoginResultCode["Unauthorized"] = 6] = "Unauthorized";
    LoginResultCode[LoginResultCode["Failed"] = 99] = "Failed";
})(LoginResultCode || (LoginResultCode = {}));
export class LoginResult {
    constructor(code, message) {
        this.code = code;
        this.message = message || '';
    }
}
/**
 * A static class (or the closest thing that TypeScript has to one) that encapsulates information
 * about the user who is logged in.
 */
export default class LoggedInUser {
    /**
     * No-op. The only purpose of this private constructor is to guarantee that
     * this class only has static members. (This is TypeScript's best approximation
     * to a static class.)
     * @private
     */
    constructor() {
    }
    /** Gets the "FirstName LastName" from userDetails, or an empty string if no userDetails. */
    static get fullName() {
        var _a, _b;
        const firstName = ((_a = LoggedInUser.userDetails) === null || _a === void 0 ? void 0 : _a.firstName) || '';
        const lastName = ((_b = LoggedInUser.userDetails) === null || _b === void 0 ? void 0 : _b.lastName) || '';
        return (firstName + ' ' + lastName).trim();
    }
    /** Tells whether there is a logged-in user. */
    static get isLoggedIn() {
        return !!LoggedInUser.userDetails;
    }
    /** Make it look like we have logged in as the given user */
    static forceLogin(userDetails) {
        this.userDetails = userDetails;
        this.enteredUsername = userDetails.username;
        this.enteredPassword = 'FakePassword';
    }
    /** Make it look like the user has logged out. */
    static logOut() {
        LoggedInUser.enteredUsername = '';
        LoggedInUser.enteredPassword = '';
        LoggedInUser.userDetails = undefined;
        LoggedInUser.jwt = undefined;
    }
    static isSystemAdmin() {
        return this.hasRole(RoleName.SystemAdmin);
    }
    static isUserAdmin() {
        return this.hasRole(RoleName.UserAdmin);
    }
    static hasRole(roleName) {
        return !!this.userDetails &&
            !!this.userDetails.roles &&
            this.userDetails.roles.some(r => r.roleName == roleName);
    }
    /**
     * Attempt to log in with the provided credentials.
     *
     * @param username The username for the login attempt.
     * @param password The entered password.
     * @param fetcher Provided to facilitate unit testing, this encloses a JavaScript
     * [fetch](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API) in an object whose behavior we can
     * control in unit tests.
     */
    static attemptLogin(username, password, fetcher) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${appConfig.sessionManagerBase}/login`;
            return (fetcher || new Fetcher()).fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'text/plain',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username: username, password: password })
            })
                .then((response) => {
                if (!response) {
                    return new LoginResult(LoginResultCode.Failed);
                }
                this.passwordExpiresSoon = false;
                if (response.ok) {
                    this.enteredUsername = username;
                    this.enteredPassword = password;
                    this.passwordExpiresSoon = response.headers.has('X-Password-Expires-Soon');
                    return (response.text().then((text) => {
                        this.jwt = text;
                        return new CloudApi(fetcher).getUserByUsername(username).then((ud) => {
                            this.userDetails = ud;
                            return this.isSystemAdmin() || this.isUserAdmin() ?
                                new LoginResult(LoginResultCode.Success) :
                                new LoginResult(LoginResultCode.Unauthorized);
                        });
                    }));
                }
                const accountLockedMessage = response.headers.get('X-Account-Locked') || '';
                if (accountLockedMessage.length > 0) {
                    return new LoginResult(LoginResultCode.AccountLocked, accountLockedMessage);
                }
                const passwordExpiredMessage = response.headers.get('X-Password-Expired') || '';
                if (passwordExpiredMessage.length > 0) {
                    return new LoginResult(LoginResultCode.PasswordExpired, passwordExpiredMessage);
                }
                const reason = response.headers.get('X-Bad-Credentials-Reason');
                if (!reason) {
                    return new LoginResult(LoginResultCode.Failed);
                }
                const countdownMessage = response.headers.get('X-Failure-Countdown') || '';
                return new LoginResult(reason == "username" ? LoginResultCode.InvalidUsername : LoginResultCode.WrongPassword, countdownMessage);
            }, () => {
                return new LoginResult(LoginResultCode.Failed);
            });
        });
    }
}
/** Username as entered on the Login page. */
LoggedInUser.enteredUsername = '';
/**
 * Password as entered on the Login page, which may or may not be correct.
 * Stored here because we don't store it in the database and we want to send it with each request.
 * A planned enhancement is to use a bearer token instead.
 */
LoggedInUser.enteredPassword = '';
/**
 * The JWT from a successful call to the /login endpoint, or undefined if there has been no successful call.
 */
LoggedInUser.jwt = undefined;
/**
 * Tells whether, according to criteria on the server, the password will expire "soon".
 */
LoggedInUser.passwordExpiresSoon = false;
/** Undefined until a user is successfully fetched from the database. */
LoggedInUser.userDetails = undefined;
//# sourceMappingURL=LoggedInUser.js.map